export type BoundaryType = "gateway" | "quarantine" | "trust_boundary" | "open";
export type BoundaryMatrix = readonly (readonly BoundaryType[])[];

export interface SystemConfig {
  readonly name: string;
  readonly regime: number;
  readonly kappa: number;
  readonly certified: boolean;
}

export interface FleetSecurityMetrics {
  readonly totalInternal: number;
  readonly totalInter: number;
  readonly totalKappa: number;
  readonly openCount: number;
  readonly fleetBounded: boolean;
  readonly allCertified: boolean;
  readonly certifiedCount: number;
}

export const FLEET_SYSTEMS: readonly SystemConfig[] = [
  { name: "Payments", regime: 1, kappa: 0, certified: true },
  { name: "API Gateway", regime: 1, kappa: 2, certified: true },
  { name: "Data Pipeline", regime: 1, kappa: 4, certified: true },
  { name: "Legacy CRM", regime: 3, kappa: 24, certified: false },
];

export const INITIAL_BOUNDARIES: BoundaryMatrix = [
  ["gateway", "gateway", "gateway", "trust_boundary"],
  ["gateway", "gateway", "quarantine", "trust_boundary"],
  ["gateway", "quarantine", "gateway", "trust_boundary"],
  ["trust_boundary", "trust_boundary", "trust_boundary", "gateway"],
];

export const BOUNDARY_LABELS: Readonly<Record<BoundaryType, string>> = {
  gateway: "Gateway (κ=0)",
  quarantine: "Quarantine (κ≤Q)",
  trust_boundary: "Trust Wall (κ=0)",
  open: "OPEN (unbounded)",
};

const BOUNDARY_ORDER: readonly BoundaryType[] = [
  "gateway",
  "quarantine",
  "trust_boundary",
  "open",
];

export function boundaryShortLabel(boundary: BoundaryType): string {
  if (boundary === "gateway") return "GW";
  if (boundary === "quarantine") return "QR";
  if (boundary === "trust_boundary") return "TW";
  return "OPEN";
}

export function boundaryAt(
  boundaries: BoundaryMatrix,
  sourceIndex: number,
  targetIndex: number,
): BoundaryType {
  return boundaries[sourceIndex]?.[targetIndex] ?? "open";
}

export function toggleBoundaryCell(
  boundaries: BoundaryMatrix,
  sourceIndex: number,
  targetIndex: number,
): BoundaryMatrix {
  const next = boundaries.map((row) => [...row]);
  const current = boundaryAt(boundaries, sourceIndex, targetIndex);
  const nextBoundary =
    BOUNDARY_ORDER[(BOUNDARY_ORDER.indexOf(current) + 1) % BOUNDARY_ORDER.length] ?? "gateway";
  next[sourceIndex][targetIndex] = nextBoundary;
  next[targetIndex][sourceIndex] = nextBoundary;
  return next;
}

export function summarizeFleetSecurity(
  systems: readonly SystemConfig[],
  boundaries: BoundaryMatrix,
): FleetSecurityMetrics {
  const totalInternal = systems.reduce((sum, system) => sum + system.kappa, 0);
  let totalInter = 0;
  let openCount = 0;

  for (let i = 0; i < systems.length; i += 1) {
    for (let j = i + 1; j < systems.length; j += 1) {
      const boundary = boundaryAt(boundaries, i, j);
      const source = systems[i];
      const target = systems[j];
      if (boundary === "open") {
        openCount += 1;
        totalInter += source.kappa + target.kappa + 10;
      } else if (boundary === "quarantine") {
        totalInter += Math.min(source.kappa, target.kappa);
      }
    }
  }

  const certifiedCount = systems.filter((system) => system.certified).length;
  return {
    totalInternal,
    totalInter,
    totalKappa: totalInternal + totalInter,
    openCount,
    fleetBounded: openCount === 0,
    allCertified: certifiedCount === systems.length,
    certifiedCount,
  };
}
